import React, { useEffect, useRef, useState, useMemo } from 'react';

// Imports nécessaires pour DuringMatch
import { MatchState, MatchAction, Player } from '../../../types';
import { SELECTABLE_TIMERS, MATCH_END_ANNOUNCEMENT_FLAG } from '../../../constants';
import { Court } from '../../molecules';
import { ColorPicker } from '../../atoms/ColorPicker';
import { useWakeLock } from '../../../hooks/useWakeLock';
import { useGameEndSender } from '../../../hooks/useGameEndSender';
import { BroadcastStatus } from '../../../hooks/useLiveBroadcast';
import { useAnnouncements } from '../../../hooks/useAnnouncements';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useBranding } from '../../../branding/BrandingContext';
import { AnnouncementBanner } from '../../atoms/AnnouncementBanner';
import { generateMatchStartAnnouncement, generateScoreAnnouncement, generateHandoutAnnouncement, generateGameEndAnnouncement, generateGameStartAnnouncement, getBallStatus } from '../../../utils/announcementGenerator';

// Import des hooks
import { useModalState, useGameFlow, useMatchActions } from './hooks';

// Import des composants UI
import { PointsTable, MatchDetails } from './components';

// Import des modals
import {
  ConductModal,
  WarningModal,
  MatchStartModal,
  GameEndModal,
  TimerSelectionModal,
  ActiveTimerModal
} from './modals';

interface DuringMatchProps {
  matchState: MatchState;
  dispatch: React.Dispatch<MatchAction>;
  wakeLock: ReturnType<typeof useWakeLock>;
  broadcastStatus: BroadcastStatus;
}

// During Match Component
export function DuringMatch({ matchState, dispatch, wakeLock, broadcastStatus }: DuringMatchProps) {
  // Hook i18n
  const { t } = useTranslation();

  // Hook branding (pour logo sponsor) - mémorisé pour éviter la perte lors des re-renders
  const { sponsorLogos } = useBranding();
  const firstSponsorLogoUrl = useMemo(() => sponsorLogos?.[0]?.url, []);

  // Hook pour les annonces arbitre
  const announcements = useAnnouncements();
  const hasShownMatchStartAnnouncement = useRef(false);

  // État pour le ColorPicker
  const [colorPicker, setColorPicker] = useState({
    isOpen: false,
    player: null as Player | null
  });

  const handleColorSelect = (color: string) => {
    if (colorPicker.player) {
      dispatch({ type: 'SET_PLAYER_COLOR', player: colorPicker.player, color });
    }
    setColorPicker({ isOpen: false, player: null });
  };

  // Hooks de gestion d'état et logique
  const modalState = useModalState(
    // Ne pas afficher la popup si on a déjà des points ou des jeux joués
    matchState.pointsTable.length === 0 && matchState.gameResults.length === 0
  );
  
  // Hook unifié pour la logique de jeu
  const gameFlow = useGameFlow({
    matchState,
    onGameEnd: (info) => {
      // Afficher l'annonce de fin de jeu
      const lines = generateGameEndAnnouncement({
        winnerName: info.winnerName,
        loserName: info.loserName,
        winnerScore: info.winnerScore,
        loserScore: info.loserScore,
        winnerGames: info.winnerGames,
        loserGames: info.loserGames,
        t,
      });
      // Afficher l'annonce avec callback pour reset scores et ouvrir la modal après sa fermeture
      announcements.showAnnouncement('game_end', lines, () => {
        dispatch({ type: 'COMPLETE_GAME_END' }); // Reset scores maintenant
        modalState.openGameEnd(info.winnerName);
      });
    },
    onMatchEnd: () => {
      // Transition immédiate vers AfterMatch.
      // L'annonce de fin de match n'est plus jouée ici : elle est rejouée sur
      // l'écran AfterMatch pour que l'arbitre ait l'écran de validation sous les
      // yeux au moment où il est le plus attentif (incite à valider le résultat).
      // On pose un drapeau de session consommé par AfterMatch : il distingue une
      // vraie transition de fin de match d'un simple rechargement de page sur
      // AfterMatch (recovery), évitant de rejouer l'annonce après un refresh.
      try {
        sessionStorage.setItem(MATCH_END_ANNOUNCEMENT_FLAG, '1');
      } catch (e) {
        // sessionStorage indisponible (mode privé strict) : l'annonce ne sera
        // simplement pas rejouée, ce qui est un dégradé acceptable.
      }
      dispatch({ type: 'COMPLETE_MATCH_END' });
    }
  });
  
  // Hook pour toutes les actions du match
  const matchActions = useMatchActions({
    matchState,
    dispatch,
    modalState,
    onServerScores: (serverScore, receiverScore) => {
      const setsToWin = matchState.matchInfo.scoringType === 'BestOf5' ? 3 : 2;
      const serverSetScore = matchState.setScores[matchState.servingPlayer!];
      const receiverSetScore = matchState.setScores[matchState.servingPlayer === 'A' ? 'B' : 'A'];
      const ballStatus = getBallStatus(serverScore, receiverScore, serverSetScore, setsToWin, receiverSetScore);
      const lines = generateScoreAnnouncement({ serverScore, receiverScore, ballStatus, t });
      announcements.showAnnouncement('score_update', lines);
    },
    onReceiverScores: (newServerScore, newReceiverScore) => {
      // Le nouveau serveur = ex-receveur, donc son setScore
      const newServer = matchState.servingPlayer === 'A' ? 'B' : 'A';
      const setsToWin = matchState.matchInfo.scoringType === 'BestOf5' ? 3 : 2;
      const serverSetScore = matchState.setScores[newServer];
      const receiverSetScore = matchState.setScores[matchState.servingPlayer!];
      const ballStatus = getBallStatus(newServerScore, newReceiverScore, serverSetScore, setsToWin, receiverSetScore);
      const lines = generateHandoutAnnouncement({ newServerScore, newReceiverScore, ballStatus, t });
      announcements.showAnnouncement('handout', lines);
    }
  });
  
  // Hook pour l'envoi silencieux des résultats de fin de jeu
  useGameEndSender({
    matchState,
    enabled: Boolean(matchState.matchInfo.resultCallbackUrl)
  });

  // broadcastStatus is received as prop from AppShell (hook lives there
  // so it survives the DuringMatch → AfterMatch screen transition)

// Wake Lock: activer pendant le match pour empêcher la mise en veille
  useEffect(() => {
    if (wakeLock.isSupported) {
      wakeLock.request();
      //console.log('[Wake Lock] Activé pour DuringMatch');
    }

    return () => {
      if (wakeLock.isActive) {
        wakeLock.release();
        //console.log('[Wake Lock] Désactivé en quittant DuringMatch');
      }
    };
  }, [wakeLock]);

  // Sync timer modal state → activeTimer for live broadcast
  useEffect(() => {
    if (modalState.timerModal.timer?.broadcastId) {
      dispatch({
        type: 'SET_ACTIVE_TIMER',
        timer: {
          id: modalState.timerModal.timer.broadcastId,
          durationSeconds: modalState.timerModal.timer.duration,
          startedAt: Math.floor(Date.now() / 1000)
        }
      });
    } else if (!modalState.timerModal.timer) {
      dispatch({ type: 'SET_ACTIVE_TIMER', timer: null });
    }
  }, [modalState.timerModal.timer, dispatch]);

  // Annonce de début de match : affichée quand la modal MatchStart se ferme
  // et que le timer warm-up break est également fermé (si utilisé)
  useEffect(() => {
    const isMatchStart = matchState.pointsTable.length === 0 && matchState.gameResults.length === 0;
    const modalIsClosed = !modalState.matchStartModal.isOpen;
    const timerIsClosed = !modalState.timerModal.isOpen && modalState.timerModal.timer === null;

    if (isMatchStart && modalIsClosed && timerIsClosed && !hasShownMatchStartAnnouncement.current && matchState.servingPlayer) {
      const lines = generateMatchStartAnnouncement({
        matchInfo: matchState.matchInfo,
        servingPlayer: matchState.servingPlayer,
        t,
      });
      announcements.showAnnouncement('match_start', lines);
      hasShownMatchStartAnnouncement.current = true;
    }
  }, [modalState.matchStartModal.isOpen, modalState.timerModal.isOpen, modalState.timerModal.timer, matchState.servingPlayer, matchState.pointsTable.length, matchState.gameResults.length, matchState.matchInfo, t, announcements]);

  // Annonce de début de jeu : affichée quand le timer se ferme ou immédiatement après "Next game"
  useEffect(() => {
    const isPending = modalState.pendingGameStart.isPending;
    const timerIsClosed = !modalState.timerModal.isOpen && modalState.timerModal.timer === null;

    if (isPending && timerIsClosed && matchState.servingPlayer) {
      const serverName = matchState.servingPlayer === 'A'
        ? matchState.matchInfo.playerA
        : matchState.matchInfo.playerB;

      const lines = generateGameStartAnnouncement({
        setScoreA: matchState.setScores.A,
        setScoreB: matchState.setScores.B,
        playerAName: matchState.matchInfo.playerA,
        playerBName: matchState.matchInfo.playerB,
        serverName,
        t,
      });
      announcements.showAnnouncement('game_start', lines);
      modalState.setPendingGameStart(false);
    }
  }, [modalState.pendingGameStart.isPending, modalState.timerModal.isOpen, modalState.timerModal.timer, matchState.servingPlayer, matchState.setScores, matchState.matchInfo, t, announcements, modalState]);

  return (
    <div className="h-screen p-4 flex flex-col overflow-hidden">
      {/* Banner d'annonce arbitre */}
      <AnnouncementBanner
        announcement={announcements.currentAnnouncement}
        onDismiss={announcements.dismissAnnouncement}
      />

      <div className="flex-1 flex flex-col min-h-0">
        {/* Players et Points Table - Layout responsive */}
        <div className="flex-1 min-h-0 flex flex-col landscape:grid landscape:grid-cols-[2fr_1fr_2fr] landscape:gap-4 landscape:items-stretch">
          {/* Player A */}
          <div className="flex-1 mb-2 landscape:mb-0">
            <Court
              player={matchState.matchInfo.playerA}
              avatarUrl={matchState.matchInfo.playerAAvatar}
              score={matchState.gameScores.A}
              setScore={matchState.setScores.A}
              color={matchState.playerAColor}
              isServing={matchState.servingPlayer === 'A'}
              servingSide={matchState.servingPlayer === 'A' ? matchState.servingSide : null}
              onSideSelect={matchActions.handleSideSelect}
              onScoreClick={() => matchActions.handleScoreClick('A')}
              canSelectSide={gameFlow.matchLogic.canSelectSide('A')}
              canChangeSide={matchState.servingPlayer === 'A' && matchState.canChangeServingSide}
              canScore={gameFlow.matchLogic.canScore('A')}
              onWarning={() => matchActions.handleWarningForPlayer('A')}
              playerSide="A"
              onColorClick={() => setColorPicker({ isOpen: true, player: 'A' })}
            />
          </div>

          {/* Player B - En mobile avant le tableau */}
          <div className="flex-1 mb-2 landscape:mb-0 landscape:order-3">
            <Court
              player={matchState.matchInfo.playerB}
              avatarUrl={matchState.matchInfo.playerBAvatar}
              score={matchState.gameScores.B}
              setScore={matchState.setScores.B}
              color={matchState.playerBColor}
              isServing={matchState.servingPlayer === 'B'}
              servingSide={matchState.servingPlayer === 'B' ? matchState.servingSide : null}
              onSideSelect={matchActions.handleSideSelect}
              onScoreClick={() => matchActions.handleScoreClick('B')}
              canSelectSide={gameFlow.matchLogic.canSelectSide('B')}
              canChangeSide={matchState.servingPlayer === 'B' && matchState.canChangeServingSide}
              canScore={gameFlow.matchLogic.canScore('B')}
              onWarning={() => matchActions.handleWarningForPlayer('B')}
              playerSide="B"
              onColorClick={() => setColorPicker({ isOpen: true, player: 'B' })}
            />
          </div>

          {/* Points table - En bas en mobile, au centre en desktop */}
          <div className="shrink-0 mb-2 landscape:mb-0 landscape:order-2">
            <PointsTable
              pointsTable={matchState.pointsTable}
              playerAColor={matchState.playerAColor}
              playerBColor={matchState.playerBColor}
              sponsorLogoUrl={firstSponsorLogoUrl}
            />
          </div>
        </div>

        {/* Match Details */}
        <MatchDetails
            matchInfo={matchState.matchInfo}
            matchDuration={gameFlow.formatTime(gameFlow.matchDuration)}
            gameDuration={gameFlow.formatTime(gameFlow.gameDuration)}
            gameResults={matchState.gameResults}
            playerAColor={matchState.playerAColor}
            playerBColor={matchState.playerBColor}
            onRollback={matchActions.handleRollback}
            broadcastStatus={broadcastStatus}
        />
      </div>

      {/* Modals */}
      <ConductModal
        isOpen={modalState.conductJustificationModal.isOpen}
        action={modalState.conductJustificationModal.action}
        playerName={modalState.conductJustificationModal.playerName}
        playerColor={modalState.conductJustificationModal.player === 'A' ? matchState.playerAColor : matchState.playerBColor}
        playerAvatar={modalState.conductJustificationModal.player === 'A' ? matchState.matchInfo.playerAAvatar : matchState.matchInfo.playerBAvatar}
        onJustification={matchActions.handleConductJustification}
        onClose={modalState.closeConductJustification}
      />

      <WarningModal
        isOpen={modalState.warningModal.isOpen}
        player={modalState.warningStep.selectedPlayer}
        playerName={modalState.warningStep.selectedPlayerName}
        playerAName={matchState.matchInfo.playerA}
        playerBName={matchState.matchInfo.playerB}
        playerAColor={matchState.playerAColor}
        playerBColor={matchState.playerBColor}
        playerAAvatar={matchState.matchInfo.playerAAvatar}
        playerBAvatar={matchState.matchInfo.playerBAvatar}
        onWarningAction={matchActions.handleWarningAction}
        onClose={modalState.closeWarning}
      />

      <MatchStartModal
        isOpen={modalState.matchStartModal.isOpen}
        onAction={matchActions.handleMatchStartAction}
      />

      <GameEndModal
        isOpen={modalState.gameEndModal.isOpen}
        winner={modalState.gameEndModal.winner}
        onAction={matchActions.handleGameEndAction}
      />
      
      <TimerSelectionModal
        isOpen={modalState.timerModal.isOpen && !modalState.timerModal.timer}
        availableTimers={SELECTABLE_TIMERS}
        onSelectTimer={modalState.openTimerWithTimer}
        onClose={modalState.closeTimer}
      />
      
      <ActiveTimerModal
        isOpen={modalState.timerModal.timer !== null}
        title={modalState.timerModal.timer?.name || ''}
        duration={modalState.timerModal.timer?.duration || 0}
        onClose={modalState.closeTimer}
        onFifteenSeconds={() => announcements.showAnnouncement('timer_fifteen_seconds', [t('announcements.timer.fifteen_seconds')])}
        onTime={() => announcements.showAnnouncement('timer_time', [t('announcements.timer.time')])}
        onPause={() => dispatch({ type: 'PAUSE_TIMER' })}
        onResume={() => dispatch({ type: 'RESUME_TIMER' })}
      />


      <ColorPicker
        isOpen={colorPicker.isOpen}
        currentColor={colorPicker.player === 'A' ? matchState.playerAColor : matchState.playerBColor}
        onColorSelect={handleColorSelect}
        onClose={() => setColorPicker({ isOpen: false, player: null })}
      />
    </div>
  );
}